import { datadogRum } from '@datadog/browser-rum';
import { useEffect, useLayoutEffect, useRef } from 'react';
import {
  detectSearchSessionBreak,
  getSearchSessionIdCookie,
  markSearchSessionIdSeeded,
  SEARCH_SESSION_ID_ROTATED_EVENT,
  setSearchSessionIdCookie
} from '~/utilities/common/searchSessionIdCookie';
import {
  armSrpResumeMarker,
  clearSrpResumeMarker,
  getSrpResumeMarker,
  isSrpExitClick
} from '~/utilities/common/srpResumeMarker';

export default function useReconcileSearchSessionId(
  searchSessionId: string,
  setSearchSessionId: (searchSessionId: string) => void,
  serverSearchSessionId?: string
) {
  const searchSessionIdRef = useRef(searchSessionId);

  useEffect(() => {
    searchSessionIdRef.current = searchSessionId;
  }, [searchSessionId]);

  // Layout effects run before the passive effects that fire requests.
  useLayoutEffect(() => {
    const searchSessionBreak = detectSearchSessionBreak(serverSearchSessionId);

    if (searchSessionBreak) {
      datadogRum.addAction(`srp-search-session-${searchSessionBreak}`, {
        hadResumeMarker: !!getSrpResumeMarker()
      });
    }

    setSearchSessionIdCookie(searchSessionIdRef.current);
    markSearchSessionIdSeeded();
    clearSrpResumeMarker();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once on mount
  }, []);

  // Leaving the SRP arms the marker the server needs to reuse the session.
  useEffect(() => {
    function handleClick(event: MouseEvent) {
      if (isSrpExitClick(event)) {
        armSrpResumeMarker(window.location.pathname);
      }
    }

    function handlePageHide() {
      armSrpResumeMarker(window.location.pathname);
    }

    document.addEventListener('click', handleClick, true);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      document.removeEventListener('click', handleClick, true);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, []);

  // Rotations can come from any hook on the page (ZIP, trade, login) or another tab.
  useEffect(() => {
    function handleRotated(event: Event) {
      if (event instanceof CustomEvent && typeof event.detail === 'string') {
        setSearchSessionId(event.detail);
      }
    }

    window.addEventListener(SEARCH_SESSION_ID_ROTATED_EVENT, handleRotated);

    return () =>
      window.removeEventListener(
        SEARCH_SESSION_ID_ROTATED_EVENT,
        handleRotated
      );
  }, [setSearchSessionId]);

  // Another tab can rotate the cookie while this page sits in bfcache.
  useEffect(() => {
    function handlePageShow(event: PageTransitionEvent) {
      if (!event.persisted) return;

      clearSrpResumeMarker();

      const cookieValue = getSearchSessionIdCookie();

      if (cookieValue) {
        setSearchSessionId(cookieValue);
      } else {
        setSearchSessionIdCookie(searchSessionIdRef.current);
      }
    }

    window.addEventListener('pageshow', handlePageShow);

    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [setSearchSessionId]);
}
